import { Button, Flex, Tooltip } from "antd";
import { ReactNode, useMemo } from "react";
import { useTranslate } from "@refinedev/core";
import { ArrowUDownLeftIcon } from "@phosphor-icons/react";
import {
  AlignBottomIcon,
  AlignCenterHorizontalIcon,
  AlignCenterVerticalIcon,
  AlignLeftIcon,
  AlignRightIcon,
  AlignTopIcon,
} from "components/icons";
import { useAntTheme } from "hooks/useAntTheme/";
import { ItemControlsConfigProvider } from "../../../controls/ItemControlsConfigProvider";
import { GroupedFontPicker } from "pages/media/components/GroupedFontPicker/GroupedFontPicker";
import {
  HorizontalTextAlign,
  ITEM_TYPES,
  VerticalTextAlign,
} from "../../../../types";
import { IncreaseDecreaseNumberInput } from "../../../controls/IncreaseDecreaseNumberInput";
import { TextUppercaseSettings } from "../../../controls/TextUppercaseSettings";
import { IconColorPicker } from "../../../controls/IconColorPicker";
import { ButtonsWrapper } from "../../ButtonWrapper";
import { useTemplateEditorStore } from "../../../../store/useTemplateEditorStore";
import { useTemplatesStatusStore } from "../../../../store/useTemplatesStatusStore";
import { useTemplateData } from "../../../../hooks/useTemplateData";
import {
  getCurrentTextAction,
  getCurrentTextItem,
} from "../../../../helpers/textHelpers";
import {
  isGradient,
  resolveFontAssetIdToBrandKitPlaceholder,
  resolveToBrandkitFontIfNeeded,
} from "../../../../helpers/itemsHelper";

const MIN_FONT_SIZE = 5; //pixels

/**
 * Grouped text style settings rendered as a single panel, mirroring the list
 * (table) tab layout: font + size on one row, colors + casing on the next, and
 * the horizontal/vertical alignment shown as button rows. Hosts the wrap text
 * and background color controls that previously lived in the settings toolbar.
 */
export const TextStyleFeature = ({
  brandKitFontsOnly = false,
}: {
  brandKitFontsOnly?: boolean;
}) => {
  const t = useTranslate();
  const { theme } = useAntTheme();

  const {
    organizationId,
    assetLibrariesData: { organizationAssetLibraries },
    brandKitData: {
      titleFont,
      textFont,
      subtitlesFont,
      brandKitPalette: palette,
    },
  } = useTemplateData();

  const organizationAssetLibraryId = organizationAssetLibraries[0]?.id;

  const brandKitFonts = useMemo(
    () =>
      [titleFont, textFont, subtitlesFont].filter(
        (asset) => asset !== undefined
      ),
    [titleFont, textFont, subtitlesFont]
  );

  const {
    titleStyle,
    textStyle,
    setTitleStyle,
    setTextStyle,
    currentItem,
    customTexts,
    updateCustomText,
    parameteredTexts,
    updateParameteredText,
    figure,
    updateFigure,
  } = useTemplateEditorStore((state) => ({
    titleStyle: state.templateState.titleStyle,
    textStyle: state.templateState.textStyle,
    setTitleStyle: state.setTitleStyle,
    setTextStyle: state.setTextStyle,
    currentItem: state.templateState.currentItem,
    customTexts: state.templateState.customTexts,
    updateCustomText: state.updateCustomText,
    parameteredTexts: state.templateState.parameteredTexts,
    updateParameteredText: state.updateParameteredText,
    figure: state.templateState.figure,
    updateFigure: state.updateFigure,
  }));

  const { addTemplateUpdate } = useTemplatesStatusStore((state) => ({
    addTemplateUpdate: state.addTemplateUpdate,
  }));

  const item = getCurrentTextItem({
    currentItem,
    titleStyle,
    textStyle,
    customTexts,
    parameteredTexts,
    figure,
  });

  const currentSetAction = getCurrentTextAction({
    currentItem,
    setTitleStyle,
    setTextStyle,
    updateCustomText,
    updateParameteredText,
    updateFigure,
    customTexts,
    parameteredTexts,
    figure,
  });

  const handleStrokeColorChange = (color?: string) => {
    currentSetAction?.({ strokeColor: color });
    addTemplateUpdate();
  };

  const handleBackgroundColorChange = (color?: string) => {
    // Font auto-fit is applied for display at render time (useFittedFontSize),
    // so only the background changes here — the stored font size stays the max.
    currentSetAction?.({ backgroundColor: color });
    addTemplateUpdate();
  };

  const showAlignment =
    currentItem?.type === ITEM_TYPES.Title ||
    currentItem?.type === ITEM_TYPES.CustomText;

  const horizontalAlignButtons: {
    key: HorizontalTextAlign;
    icon: ReactNode;
  }[] = [
    { key: "left", icon: <AlignLeftIcon /> },
    { key: "center", icon: <AlignCenterHorizontalIcon /> },
    { key: "right", icon: <AlignRightIcon /> },
  ];

  const verticalAlignButtons: {
    key: VerticalTextAlign;
    icon: ReactNode;
  }[] = [
    { key: "top", icon: <AlignTopIcon /> },
    { key: "center", icon: <AlignCenterVerticalIcon /> },
    { key: "bottom", icon: <AlignBottomIcon /> },
  ];

  return (
    <ItemControlsConfigProvider>
      <Flex vertical gap={12}>
        <Flex align="center" gap={8}>
          <GroupedFontPicker
            value={resolveToBrandkitFontIfNeeded(
              item?.fontAssetId,
              titleFont,
              subtitlesFont,
              textFont
            )}
            brandKitFonts={brandKitFonts}
            assetLibraryId={organizationAssetLibraryId}
            organizationId={organizationId}
            brandKitOnly={brandKitFontsOnly}
            onChange={(path, fontAssetId) => {
              const assetId = resolveFontAssetIdToBrandKitPlaceholder(
                fontAssetId,
                brandKitFonts
              );
              currentSetAction?.({
                fontPath: path,
                fontAssetId: assetId,
              });
              addTemplateUpdate();
            }}
            style={{ width: 200 }}
          />
          <IncreaseDecreaseNumberInput
            value={item?.fontSize}
            min={MIN_FONT_SIZE}
            style={{ width: 100 }}
            onChange={(value) => {
              if (typeof value === "number") {
                // Store the user's intended (max) size; the auto-fit shrink is
                // applied for display at render time (useFittedFontSize).
                currentSetAction?.({ fontSize: value });
                addTemplateUpdate();
              }
            }}
          />
        </Flex>
        <Flex align="center" gap={8}>
          {palette && (
            <>
              <IconColorPicker
                value={item?.color}
                allowClear={false}
                disabledAlpha={true}
                onChangeComplete={(resolvedColor) => {
                  if (isGradient(resolvedColor)) return;
                  currentSetAction?.({ color: resolvedColor });
                  addTemplateUpdate();
                }}
                title={t("components.templates.settings.textColor")}
                palette={palette}
                type="text"
                hint={t(
                  "components.templates.settings.TextControls.iconColorPicker.text"
                )}
              />
              <IconColorPicker
                value={item?.strokeColor}
                allowClear
                disabledAlpha={true}
                onClear={() => handleStrokeColorChange()}
                onChangeComplete={(resolvedColor) => {
                  if (isGradient(resolvedColor)) return;
                  handleStrokeColorChange(resolvedColor);
                }}
                title={t("components.templates.settings.strokeColor")}
                palette={palette}
                type="stroke"
                hint={t(
                  "components.templates.settings.TextControls.iconColorPicker.stroke"
                )}
              />
              <IconColorPicker
                value={item?.backgroundColor}
                allowClear
                onClear={() => handleBackgroundColorChange()}
                onChangeComplete={(resolvedColor) => {
                  if (isGradient(resolvedColor)) return;
                  handleBackgroundColorChange(resolvedColor);
                }}
                title={t("components.templates.settings.textBackground")}
                palette={palette}
                type="background"
                hint={t(
                  "components.templates.settings.TextControls.iconColorPicker.background"
                )}
              />
            </>
          )}
          <TextUppercaseSettings
            value={item?.upperCase}
            onChange={(value) => {
              currentSetAction?.({ upperCase: value });
              addTemplateUpdate();
            }}
          />
          {showAlignment && (
            <Tooltip
              title={t("components.templates.settings.wrapText")}
              trigger={["hover"]}
            >
              <Button
                icon={<ArrowUDownLeftIcon size={16} />}
                type="text"
                onClick={() => {
                  currentSetAction?.({ wrapText: !(item?.wrapText ?? true) });
                  addTemplateUpdate();
                }}
                style={{
                  backgroundColor:
                    (item?.wrapText ?? true) ? theme.colorBgBase : undefined,
                }}
                data-testid="wrap-text-button"
              />
            </Tooltip>
          )}
        </Flex>
        {showAlignment && (
          <Flex gap={12} vertical align="start">
            <ButtonsWrapper $hasBackground>
              {horizontalAlignButtons.map((button) => (
                <Button
                  data-testid="text-alignment-button"
                  key={button.key}
                  icon={button.icon}
                  type="text"
                  onClick={() => {
                    currentSetAction?.({ textAlign: button.key });
                    addTemplateUpdate();
                  }}
                  style={{
                    height: 24,
                    width: 32,
                    backgroundColor:
                      (item?.textAlign ?? "center") === button.key
                        ? theme.colorBgBase
                        : undefined,
                  }}
                />
              ))}
            </ButtonsWrapper>
            {item?.size.height != null && (
              <ButtonsWrapper $hasBackground>
                {verticalAlignButtons.map((button) => (
                  <Button
                    data-testid="text-alignment-button"
                    key={button.key}
                    icon={button.icon}
                    type="text"
                    onClick={() => {
                      currentSetAction?.({ verticalAlign: button.key });
                      addTemplateUpdate();
                    }}
                    style={{
                      height: 24,
                      width: 32,
                      backgroundColor:
                        (item.verticalAlign ?? "center") === button.key
                          ? theme.colorBgBase
                          : undefined,
                    }}
                  />
                ))}
              </ButtonsWrapper>
            )}
          </Flex>
        )}
      </Flex>
    </ItemControlsConfigProvider>
  );
};
